import { useState, useEffect, useCallback } from 'react';
import { useLang } from '../contexts/LangContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, FolderOpen } from 'lucide-react';
import TablePagination from '../components/TablePagination';

export default function CategoryPage() {
  const { t } = useLang();
  const [categories, setCategories] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '' });
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  const loadCategories = useCallback(async (page = 1) => {
    try {
      const res = await api.listCategories(page, 20);
      setCategories(res.data.data);
      setPagination({ total: res.data.total, page: res.data.page, limit: res.data.limit, total_pages: res.data.total_pages });
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await api.updateCategory(editing.id, form); }
      else { await api.createCategory(form); }
      toast.success(t('success'));
      closeDialog();
      loadCategories(pagination.page);
    } catch (err) { toast.error(err?.response?.data?.detail || t('error')); }
  };

  const handleEdit = (cat) => {
    setEditing(cat);
    setForm({ name: cat.name, description: cat.description || '' });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false); setEditing(null);
    setForm({ name: '', description: '' });
  };

  const handleDelete = async () => {
    try { await api.deleteCategory(deleteId); toast.success(t('success')); setDeleteId(null); loadCategories(pagination.page); }
    catch (err) { toast.error(t('error')); }
  };

  const filtered = categories.filter(c =>
    c.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="category-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#202020]" style={{ fontFamily: 'Manrope' }}>Kategori</h1>
          <p className="text-sm text-[#64584F]">{pagination.total} kategori</p>
        </div>
        <Button onClick={() => { closeDialog(); setDialogOpen(true); }} className="bg-[#EB7B44] hover:bg-[#D96A35] text-white font-medium" data-testid="add-category-btn">
          <Plus size={16} className="mr-2" /> {t('add')} Kategori
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A6A2A0]" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={`${t('search')}...`} className="pl-9 bg-[#F6F5F5] border-transparent focus:bg-white focus:border-[#EB7B44]" data-testid="category-search-input" />
      </div>

      <Card className="bg-white border-[#CACACC] rounded-xl shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F6F5F5] border-b border-[#CACACC]">
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider">{t('name')}</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider hidden sm:table-cell">Deskripsi</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider hidden md:table-cell">{t('date')}</TableHead>
              <TableHead className="text-xs font-bold text-[#64584F] uppercase tracking-wider text-right">{t('actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} className="text-center py-12 text-[#A6A2A0]">{t('loading')}</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-12">
                  <FolderOpen size={32} className="mx-auto text-[#CACACC] mb-2" />
                  <p className="text-[#A6A2A0]">{t('no_data')}</p>
                </TableCell>
              </TableRow>
            ) : filtered.map(cat => (
              <TableRow key={cat.id} className="border-b border-[#DFDEDE] hover:bg-[#F6F5F5]/50 transition-colors" data-testid={`category-row-${cat.id}`}>
                <TableCell className="font-medium text-[#202020]">{cat.name}</TableCell>
                <TableCell className="text-[#64584F] hidden sm:table-cell">{cat.description || '-'}</TableCell>
                <TableCell className="text-[#64584F] text-sm hidden md:table-cell">{cat.created_at?.split('T')[0]}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(cat)} data-testid={`category-edit-btn-${cat.id}`}>
                      <Pencil size={15} className="text-[#64584F]" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteId(cat.id)} data-testid={`category-delete-btn-${cat.id}`}>
                      <Trash2 size={15} className="text-red-500" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination page={pagination.page} totalPages={pagination.total_pages} total={pagination.total} limit={pagination.limit} onPageChange={(p) => loadCategories(p)} />
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={closeDialog}>
        <DialogContent className="bg-white border-[#CACACC] rounded-xl max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#202020]" style={{ fontFamily: 'Manrope' }}>
              {editing ? t('edit') : t('add')} Kategori
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#202020]">{t('name')}</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="border-[#CACACC]" data-testid="category-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-[#202020]">Deskripsi</Label>
              <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="border-[#CACACC]" data-testid="category-desc-input" />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={closeDialog} className="border-[#CACACC]">{t('cancel')}</Button>
              <Button type="submit" className="bg-[#EB7B44] hover:bg-[#D96A35] text-white" data-testid="category-save-btn">{t('save')}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="bg-white border-[#CACACC] rounded-xl max-w-sm">
          <DialogHeader><DialogTitle className="text-[#202020]">{t('confirm_delete')}</DialogTitle></DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteId(null)} className="border-[#CACACC]">{t('cancel')}</Button>
            <Button onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white" data-testid="confirm-delete-category-btn">{t('delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
